import React from "react";
import { Form, FormGroup } from "semantic-ui-react";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { Week } from "../../../../../../../api";
import { useAuth } from "../../../../../../../hooks";
import { initialValues, validationSchema } from "./NewWeekForm.form";
const weekController = new Week();

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  },
});

export function NewWeekForm(props) {
  const { onClose, onReloadWeek, cantWeeks, week } = props;
  const { syllabuSubjectGradeId } = useParams();
  const { accessToken } = useAuth();

  /**
   * Initializes and manages the form for creating a new week within a syllabus subject grade using the useFormik hook.
   *
   * This form is designed to capture the title for a new week and automatically assign it an index based on the current number of weeks (`cantWeeks`). It also associates the new week with a specific syllabus subject grade identified by `syllabuSubjectGradeId`.
   *
   * Form Initialization:
   * - `initialValues`: Sets up the initial form values, which may include a default title if editing an existing week or placeholders for a new week.
   * - `validationSchema`: Defines the validation rules for the form fields, ensuring data integrity before submission.
   *
   * Form Submission (`onSubmit`):
   * 1. Constructs a `data` object containing the new week's title, its index (determined by the current count of weeks), and the associated syllabus subject grade ID.
   * 2. Calls `weekController.createWeek` with the user's access token and the `data` object to create the new week in the backend.
   * 3. Displays a success message using `Toast.fire` upon successful creation, including the week's index and title for user confirmation.
   * 4. Triggers `onReloadWeek` to refresh the list of weeks, ensuring the UI reflects the newly added week.
   * 5. Closes the form modal or dialog (`onClose`), returning the user to the previous UI context.
   *
   * Error Handling:
   * - Catches and logs any errors to the console if the week creation process fails, providing feedback for debugging.
   */
  const formik = useFormik({
    initialValues: initialValues(week),
    validationSchema: validationSchema(),
    onSubmit: async (formValue) => {
      try {
        const data = {
          title: formValue.title,
          index: cantWeeks,
          syllabuSubjectGradeId: syllabuSubjectGradeId,
        };
        await weekController.createWeek(accessToken, data);

        Toast.fire({
          icon: "success",
          title: `Se ha creado la unidad ${cantWeeks + 1}: ${formValue.title}.`,
        });
        onReloadWeek();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Form.Input
        name="title"
        label="Título de la unidad"
        onChange={formik.handleChange}
        value={formik.values.title}
        error={formik.errors.title}
      />
      <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
        Crear unidad
      </Form.Button>
    </Form>
  );
}
